/**
 * PulseGutterRail — the Pulse as a labeled instrument READ-OUT floating in the
 * far-right margin well (the secondary nav's slot), replacing the in-flow
 * canonical bar (`PulseNavShell`/`PulseNavBar`) as the mode-page pulse
 * presentation. Productized from `/styleguide/pulse-gutter` at the tuned pick
 * (Matt, 2026-07-17): LEDGER style · COMPACT density · 208px · EDGE position.
 *
 * The design, frozen from the tuner:
 *   · Every touchpoint is a NAMED row — muted label at the nav sub-item type
 *     (type-subtext tracking-tight, the mode rail's Issues/Goals/Projects rows),
 *     muted 11px tabular numeral right, a 2px luminous data-line (the shipped
 *     `pulseBarFill` warm ramp) over a hairline track, a target notch at the
 *     touch's target. Ghost (uninstrumented) = dashed hairline + em-dash numeral.
 *   · Section captions are white, sentence-case at the same nav type (type-subtext
 *     medium — matches the navigation's letter casing, not uppercase), NO rules,
 *     with the section rollup numeral right — 10px margin below.
 *   · No panel, no spine, no header block, no back affordance, no insight
 *     callouts (those re-home elsewhere).
 *   · The rail hangs at the chrome's 16px edge inset — its figure column ends
 *     on the same line as the top-right header buttons — vertically centered,
 *     outside the page flow (`fixed`; the page never reserves space for it).
 *
 * Every row and caption is a LINK: cursor-pointer, click navigates to the
 * touchpoint's / section's page via the server-computed `href` (`router.push`
 * — same contract as the bar it replaces). The real cursor tooltip rides the
 * rows via the `data-pulse-bar` / `data-pulse-cluster` contract
 * (`WallTipLayer`). Hovering a section quiets the others.
 *
 * Data: the same `pulse.aggregate` SWR read the bar used (`keepPreviousData`
 * — old rows hold while a scope change loads). Desktop-only (`xl:` — the
 * ContextRail precedent); below xl the rail simply doesn't render, and being
 * position:fixed it needs no skeleton (nothing shifts while it loads).
 */

import * as React from "react";
import { useNavigate as useRouter, usePathname, useSearch as useSearchParams } from "../../router";
import { cn } from "../../utils";
import { useHydrated } from "../../use-hydrated";
import { apiCall, useApi } from "../../api/client";
import { pulse } from "../../api/contracts/pulse";
import { pulseTelemetry } from "../../api/contracts/pulse-telemetry";
import { play } from "../../api/contracts/play";
import { Button } from "../../button";
import { Icon } from "../../icon";
import { pulseBarFill, type PulseProps } from "./pulse-wall-shared";
import { WallTipLayer } from "./pulse-wall-tips";
import { InsightDialog, fmtTelemetry } from "./insight-dialog";
import type { PulseGroupDTO, PulseTouchDTO } from "../../api/contracts/pulse";
import type {
  SectionTelemetryDTO,
  TelemetryAnchorDTO,
  TelemetryFindingDTO,
  TelemetrySeriesDTO,
} from "../../api/contracts/pulse-telemetry";

// The tuned geometry (Ledger · Compact · 208 · Edge — /styleguide/pulse-gutter).
const RAIL_W = 208;
const EDGE_INSET = 16; // the chrome's own edge padding (header buttons / logo)
const LINE_H = 2; // the lit data-line
const LABEL_GAP = "calc(var(--spacing) * 1.5)"; // label baseline → data-line
const LABEL_HAIRLINE_GAP = "calc(var(--spacing) * 1.5 + 1px)";
const ROW_PAD = "calc(var(--spacing) * 0.5)";
const ROW_PAD_TRAILING = "var(--spacing)";
const ROW_PAD_OPEN = "calc(var(--spacing) * 1.5)";
const SEC_GAP = "calc(var(--spacing) * 3.5)"; // between sections
const DEFAULT_TARGET = 70;

export type PulseGutterRailProps = {
  /** The pulse scope: a mode slug (`"work"`) or a drilled domain (`"work:queue"`). */
  scope: string;
  weight?: PulseProps["weight"];
  days?: PulseProps["days"];
  projectSlug?: string | null;
  className?: string;
  /**
   * Data plane (docs/pulse/DYNAMIC-PULSE-INTEGRATION-PRD.md §4 P3). Absent/false
   * ⇒ the classic target-attainment rows (byte-identical to before this prop, its
   * own `pulse.aggregate` read included). True ⇒ the Dynamic Pulse instrument
   * (learned-baseline findings) in the SAME rail geometry, with a clean fallback
   * to the classic rows when no stream serves the scope. Gated at the mounts by
   * `pulse.dynamic.enabled` (default ON; the kill switch reverts to classic).
   */
  dynamic?: boolean;
};

/**
 * The gutter rail's provider switch. `dynamic` picks the data plane behind the
 * SAME 208px geometry — classic (default) vs the Dynamic Pulse instrument — and
 * each plane owns its own fetch/hooks in a separate component, so `dynamic={false}`
 * never touches the telemetry read and stays byte-for-byte the shipped rail.
 */
export function PulseGutterRail({ dynamic, ...props }: PulseGutterRailProps) {
  return dynamic ? <PulseGutterRailDynamic {...props} /> : <PulseGutterRailClassic {...props} />;
}

function PulseGutterRailClassic({ scope, weight, days, projectSlug, className }: PulseGutterRailProps) {
  const router = useRouter();
  const hydrated = useHydrated();
  const { data } = useApi(
    pulse.aggregate,
    { params: { scope }, query: { weight, days, projectSlug: projectSlug ?? undefined } },
    { keepPreviousData: true },
  );
  const groups = React.useMemo(
    () => (data?.groups ?? []).filter((g) => g.touches.length > 0),
    [data],
  );
  const [hoverKey, setHoverKey] = React.useState<string | null>(null);

  // Fixed overlay — nothing in the page flow depends on it, so no skeleton:
  // render nothing until the data is here (and below xl via CSS).
  if (!hydrated || groups.length === 0) return null;

  const open = (href: string | null | undefined) => {
    if (href) router.push(href, { scroll: false });
  };

  return (
    <aside
      aria-label="Pulse"
      // Marks the rail for `.page-scroll-row:has([data-pulse-rail])` (globals.css)
      // — widens `--content-inset` so the centered content clears the instrument.
      data-pulse-rail=""
      className={cn("pointer-events-none fixed inset-y-0 z-30 hidden items-center xl:flex", className)}
      style={{ right: EDGE_INSET }}
    >
      <div className="no-scrollbar pointer-events-auto max-h-[88vh] overflow-y-auto py-4" style={{ width: RAIL_W }}>
        <WallTipLayer groups={groups} tipSide="below">
          <div>
            {groups.map((g) => (
              <RailGroup
                key={g.key}
                group={g}
                dim={hoverKey != null && hoverKey !== g.key}
                onHover={setHoverKey}
                onOpen={open}
              />
            ))}
          </div>
        </WallTipLayer>
      </div>
    </aside>
  );
}

function RailGroup({
  group,
  dim,
  onHover,
  onOpen,
}: {
  group: PulseGroupDTO;
  dim: boolean;
  onHover: (key: string | null) => void;
  onOpen: (href: string | null | undefined) => void;
}) {
  return (
    <section
      data-pulse-cluster={group.key}
      className="transition-opacity duration-150 ease-out last:mb-0"
      style={{ opacity: dim ? 0.4 : 1, marginBottom: SEC_GAP }}
      onMouseEnter={() => onHover(group.key)}
      onMouseLeave={() => onHover(null)}
    >
      {/* button-ignore: rail section caption — a nav link to the section's page,
          part of the gutter instrument (no Button variant applies). */}
      <button
        type="button"
        onClick={group.href ? () => onOpen(group.href) : undefined}
        aria-label={`Open ${group.label}`}
        className={cn(
          "mb-2.5 flex w-full items-baseline justify-between gap-2 outline-none focus-visible:ring-1 focus-visible:ring-primary/40",
          group.href ? "cursor-pointer" : "cursor-default",
        )}
      >
        <span className="min-w-0 truncate type-subtext font-medium leading-none tracking-tight text-foreground">
          {group.label}
        </span>
        <span className="shrink-0 type-subtext leading-none tabular-nums text-foreground">{group.health ?? "—"}</span>
      </button>
      <div>
        {group.touches.map((t) => (
          <RailRow key={t.id} touch={t} fallbackHref={group.href} onOpen={onOpen} />
        ))}
      </div>
    </section>
  );
}

function RailRow({
  touch,
  fallbackHref,
  onOpen,
}: {
  touch: PulseTouchDTO;
  fallbackHref?: string | null;
  onOpen: (href: string | null | undefined) => void;
}) {
  const ghost = touch.health == null;
  const target = touch.target ?? DEFAULT_TARGET;
  const fill = pulseBarFill(touch.health, target);
  const href = touch.href ?? fallbackHref;
  const width = ghost ? 0 : Math.min(100, Math.max(0, touch.health!));
  return (
    // button-ignore: rail row — a nav link to the touchpoint's page (a labeled
    // data-line in the gutter instrument, not a form action).
    <button
      type="button"
      data-pulse-bar={touch.id}
      onClick={href ? () => onOpen(href) : undefined}
      aria-label={`Open ${touch.label}`}
      className={cn(
        "group/bar block w-full text-left outline-none focus-visible:ring-1 focus-visible:ring-primary/40",
        href ? "cursor-pointer" : "cursor-default",
      )}
      style={{ paddingTop: ROW_PAD, paddingBottom: ROW_PAD_TRAILING }}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span
          className={cn(
            "min-w-0 truncate type-subtext leading-none tracking-tight transition-colors",
            ghost ? "text-muted-foreground/60" : "text-muted-foreground group-hover/bar:text-foreground",
          )}
        >
          {touch.label}
        </span>
        <span className="w-7 shrink-0 text-right type-subtext leading-none tabular-nums text-muted-foreground">
          {ghost ? "—" : touch.health}
        </span>
      </div>
      {ghost ? (
        <div className="w-full border-b border-dashed border-border/40" style={{ marginTop: LABEL_HAIRLINE_GAP, height: 1 }} />
      ) : (
        <div className="relative w-full rounded-full bg-border/60" style={{ marginTop: LABEL_GAP, height: LINE_H }}>
          {/* the lit data-line — the shipped warm-ramp paint */}
          <div
            className="absolute inset-y-0 left-0 rounded-full"
            style={{ width: `${width}%`, background: fill.background, boxShadow: fill.boxShadow }}
          />
          {/* target notch */}
          <div className="absolute top-1/2 h-1.5 w-px -translate-y-1/2 bg-foreground/35" style={{ left: `${target}%` }} />
        </div>
      )}
    </button>
  );
}

// ══ Dynamic plane (docs/pulse/DYNAMIC-PULSE-INTEGRATION-PRD.md §4 P3 · P7) ═════
// Inverted data plane behind the PEEK MORPH: at rest the rail is a bare ~28px
// sliver strip on the right edge — one pace sliver for the section's ANCHOR (its
// output rate vs its own learned baseline — Pivotal velocity) plus one sliver
// per SURFACED FINDING (deviants only; length = |z|/8, colored by kind).
// Hovering pops the 208px labeled instrument via a coordinated width morph
// (mechanics + timings ported VERBATIM from app/styleguide/dynamic-pulse-peek):
// anchor numeral + pace, the attention rows (hover → causes), and the quiet
// footer whose count line discloses the full quiet list. The anchor + each row
// open the InsightDialog (a MODAL, not a navigation); `?insight=` opens it
// directly without popping the rail. When no stream serves the scope — or the
// section carries no series — this falls back to the classic ledger rows,
// identical to `dynamic={false}` (its own `pulse.aggregate` read included).

// Row fill by finding kind — mirrors the styleguide prototype
// (app/styleguide/dynamic-pulse-rail/_rail.tsx KIND_FILL): a high regression is
// alarming (danger), a medium one cautionary (warning), an improvement good, a
// neutral shift informational.
const KIND_FILL: Record<string, string> = {
  "regression-high": "bg-danger",
  "regression-medium": "bg-warning",
  improvement: "bg-success",
  shift: "bg-info",
};

function findingFill(f: TelemetryFindingDTO): string {
  if (f.kind === "regression") return KIND_FILL[`regression-${f.severity}`]!;
  return KIND_FILL[f.kind]!;
}

// ── peek morph mechanics (ported VERBATIM from the POC —
// app/styleguide/dynamic-pulse-peek/_peek.tsx; Matt tuned these constants) ─────
// At rest the rail is a bare ~28px sliver strip; hover pops it to the 208px
// labeled instrument via a coordinated width morph. Timings are the POC's exactly.
const PEEK_W = 28; // the resting sliver strip width
const ANCHOR_LINE_H = 3; // the anchor pace sliver (thicker than a finding line)
const CLOSE_GRACE_MS = 160; // leave-grace before the rail folds back
const ARM_DELAY_MS = 1000; // ignore hover for ~1s after load (no pop on page entry)
const SETTLE_MS = 350; // width-morph settle before rows click / causes popovers arm
const MORPH = "duration-300 ease-[cubic-bezier(.32,.72,.28,1)] motion-reduce:!transition-none";

const prefersReduce = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** The anchor's pace vs its own baseline as a peek sliver: bg tone + phrase. */
function paceMeta(ratio: number | null): { cls: string; text: string } {
  // No current reading: a neutral sliver and an honest phrase. Drawing the
  // "on pace" tone here would assert a comparison that was never made.
  if (ratio === null) return { cls: "bg-muted-foreground/40", text: "no activity this week" };
  if (ratio >= 1.2)
    return { cls: "bg-success", text: `${ratio >= 10 ? Math.round(ratio) : ratio.toFixed(1)}× your baseline` };
  if (ratio >= 0.8) return { cls: "bg-muted-foreground", text: "on pace with your baseline" };
  return { cls: "bg-danger", text: `${ratio.toFixed(1)}× your baseline` };
}

/** Zero-footprint text collapser (grid-rows 0fr↔1fr) — the POC's. */
function Collapser({ open, children }: { open: boolean; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        "grid transition-[grid-template-rows,opacity]",
        MORPH,
        open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
      )}
      inert={!open}
    >
      <div className="overflow-hidden">{children}</div>
    </div>
  );
}

// The section's ANCHOR as a peek block: a right-anchored pace sliver at rest,
// morphing to the labeled numeral + pace on open. Clicking (once popped) opens
// the InsightDialog for the anchor series.
function AnchorPeek({
  anchor,
  open,
  onOpenInsight,
}: {
  anchor: TelemetryAnchorDTO;
  open: boolean;
  onOpenInsight: (id: string) => void;
}) {
  const pace = paceMeta(anchor.ratio);
  // Pace sliver: 1× baseline = half length, ≥2× = full, fading short when slow.
  // No reading = no fill; an empty track reads as "nothing measured", where any
  // non-zero width would read as a measured pace.
  const fillPct = anchor.ratio === null ? 0 : Math.min(100, anchor.ratio * 50);
  return (
    <div className={cn("pb-1.5", open && "cursor-pointer")} onClick={() => open && onOpenInsight(anchor.id)}>
      <Collapser open={open}>
        <div className="pb-1.5">
          <p className="type-subtext font-medium leading-none tracking-tight text-foreground">{anchor.label}</p>
          <p className="mt-1.5 type-title font-medium leading-display tabular-nums">{fmtTelemetry(anchor.unit, anchor.recent)}</p>
          <p className="type-subtext tabular-nums text-muted-foreground">{pace.text}</p>
        </div>
      </Collapser>
      <div className="relative w-full" style={{ height: ANCHOR_LINE_H }}>
        <div
          className={cn("absolute inset-0 rounded-full bg-border/60 transition-opacity", MORPH, open ? "opacity-100" : "opacity-0")}
        />
        <div
          className={cn("absolute inset-y-0 rounded-full transition-[width,left]", MORPH, pace.cls)}
          style={{ left: open ? 0 : `${100 - fillPct}%`, width: `${fillPct}%` }}
        />
      </div>
    </div>
  );
}

// One SURFACED finding as a peek row: right-anchored |z| sliver (colored by kind)
// at rest, morphing to a labeled row on open; once settled, hovering shows the
// causes popover and clicking opens the InsightDialog for the series.
function FindingPeekRow({
  f,
  open,
  settled,
  onOpenInsight,
}: {
  f: TelemetryFindingDTO;
  open: boolean;
  settled: boolean;
  onOpenInsight: (id: string) => void;
}) {
  const s = f.series;
  const fillPct = Math.min(100, (Math.abs(s.z) / 8) * 100);
  return (
    <div
      data-pulse-bar={s.id}
      className={cn("group/row relative transition-[padding]", MORPH, settled && "cursor-pointer")}
      style={{ paddingTop: open ? ROW_PAD_OPEN : ROW_PAD, paddingBottom: open ? ROW_PAD_OPEN : ROW_PAD }}
      onClick={() => settled && onOpenInsight(s.id)}
    >
      <Collapser open={open}>
        <div className="flex items-baseline justify-between gap-2" style={{ paddingBottom: "calc(var(--spacing) * 1.5)" }}>
          <span
            className={cn(
              "min-w-0 truncate type-subtext leading-none tracking-tight text-muted-foreground transition-colors",
              settled && "group-hover/row:text-foreground",
            )}
          >
            {s.label}
          </span>
          <span className="shrink-0 type-subtext leading-none tabular-nums text-muted-foreground">
            {fmtTelemetry(s.unit, s.recent)}
          </span>
        </div>
      </Collapser>
      <div className="relative w-full" style={{ height: LINE_H }}>
        <div
          className={cn("absolute inset-0 rounded-full bg-border/40 transition-opacity", MORPH, open ? "opacity-100" : "opacity-0")}
        />
        <div
          className={cn("absolute inset-y-0 rounded-full transition-[width,left]", MORPH, findingFill(f))}
          style={{ left: open ? 0 : `${100 - fillPct}%`, width: `${fillPct}%` }}
        />
      </div>

      {/* causes peek — only once the pop-out has settled */}
      {settled && (
        <div className="pointer-events-none absolute right-full top-1/2 z-20 mr-5 hidden w-72 -translate-y-1/2 rounded-md border border-border bg-popover p-3 text-left shadow-lg group-hover/row:block">
          <p className="type-subtext font-medium">{s.label}</p>
          <p className="mt-0.5 type-subtext text-muted-foreground">
            {fmtTelemetry(s.unit, s.baseline)} normal → <span className="font-medium text-foreground">{fmtTelemetry(s.unit, s.recent)}</span> this week
          </p>
          {f.causes.length > 0 && (
            <div className="mt-1.5 space-y-0.5 border-t border-border/40 pt-1.5">
              {f.causes.map((c) => (
                <p key={c.label} className="type-subtext text-muted-foreground">
                  <span className="text-foreground/80">{c.label}</span>{" "}
                  {fmtTelemetry(s.unit, c.baselinePerDay)} → {fmtTelemetry(s.unit, c.recentPerDay)} ·{" "}
                  {Math.round(Math.abs(c.share) * 100)}% of the move
                </p>
              ))}
            </div>
          )}
          <p className="mt-1.5 type-subtext text-muted-foreground/70">
            z {s.z.toFixed(1)} · coupling to anchor {(s.coupling ?? 0).toFixed(2)}
          </p>
        </div>
      )}
    </div>
  );
}

// One QUIET series row inside the popped panel's disclosure (muted label + unit
// numeral). Clicking opens the InsightDialog — it resolves quiet series too.
function QuietPeekRow({
  s,
  settled,
  onOpenInsight,
}: {
  s: TelemetrySeriesDTO;
  settled: boolean;
  onOpenInsight: (id: string) => void;
}) {
  return (
    <div
      data-pulse-bar={s.id}
      className={cn("group/quiet flex items-baseline justify-between gap-2 py-1", settled && "cursor-pointer")}
      onClick={() => settled && onOpenInsight(s.id)}
    >
      <span
        className={cn(
          "min-w-0 truncate type-subtext leading-none tracking-tight text-muted-foreground/80 transition-colors",
          settled && "group-hover/quiet:text-foreground",
        )}
      >
        {s.label}
      </span>
      <span className="shrink-0 type-subtext leading-none tabular-nums text-muted-foreground/70">
        {fmtTelemetry(s.unit, s.recent)}
      </span>
    </div>
  );
}

function PulseGutterRailDynamic({ scope, weight, days, projectSlug, className }: PulseGutterRailProps) {
  const hydrated = useHydrated();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { data, mutate } = useApi(
    pulseTelemetry.section,
    { params: { scope } },
    { keepPreviousData: true },
  );
  const [clicked, setClicked] = React.useState<string | null>(null);

  // ── peek morph state machine (ported from the POC PeekRail) ──────────────────
  // `open` drives the width/label morph; `settled` (SETTLE_MS after open) arms
  // the row clicks + causes popovers; `quietOpen` is the in-panel quiet-list
  // disclosure. All three reset when the rail folds back.
  const [open, setOpen] = React.useState(false);
  const [settled, setSettled] = React.useState(false);
  const [quietOpen, setQuietOpen] = React.useState(false);
  const openRef = React.useRef(false);
  const closeRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const settleRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const armedRef = React.useRef(false);

  React.useEffect(() => {
    const t = setTimeout(() => {
      armedRef.current = true;
    }, ARM_DELAY_MS);
    return () => clearTimeout(t);
  }, []);
  React.useEffect(
    () => () => {
      if (closeRef.current) clearTimeout(closeRef.current);
      if (settleRef.current) clearTimeout(settleRef.current);
    },
    [],
  );

  const enter = React.useCallback(() => {
    if (!armedRef.current) return;
    if (closeRef.current) clearTimeout(closeRef.current);
    if (!openRef.current) {
      openRef.current = true;
      setOpen(true);
      if (settleRef.current) clearTimeout(settleRef.current);
      settleRef.current = setTimeout(() => setSettled(true), prefersReduce() ? 0 : SETTLE_MS);
    }
  }, []);
  const close = React.useCallback(() => {
    openRef.current = false;
    if (settleRef.current) clearTimeout(settleRef.current);
    setSettled(false);
    setOpen(false);
    setQuietOpen(false);
  }, []);
  const leave = React.useCallback(() => {
    if (closeRef.current) clearTimeout(closeRef.current);
    if (prefersReduce()) {
      close();
      return;
    }
    closeRef.current = setTimeout(close, CLOSE_GRACE_MS);
  }, [close]);

  const section = data?.section ?? null;
  const hasSeries =
    !!section && (!!section.anchor || section.surfaced.length > 0 || section.quiet.length > 0);

  // Deep-link: `?insight=<seriesId>` opens the modal at BOTH mounts, but only for
  // a series that belongs to THIS section (the rail reads its own scope). A local
  // click takes precedence; closing clears whichever param opened it.
  const urlInsight = searchParams?.get("insight") ?? null;
  const belongs = React.useMemo(() => {
    const ids = new Set<string>();
    if (section) {
      if (section.anchor) ids.add(section.anchor.id);
      for (const f of section.surfaced) ids.add(f.series.id);
      for (const s of section.quiet) ids.add(s.id);
    }
    return (id: string | null) => !!id && ids.has(id);
  }, [section]);
  const openId = clicked ?? (belongs(urlInsight) ? urlInsight : null);

  // The open series' full DTO (quiet ∪ surfaced — mirrors InsightDialog's own
  // lookup), so the footer actions can read its watched/viewHref/label without
  // re-deriving them.
  const openSeries = React.useMemo<TelemetrySeriesDTO | null>(() => {
    if (!section || !openId) return null;
    const quiet = section.quiet.find((s) => s.id === openId);
    if (quiet) return quiet;
    return section.surfaced.find((f) => f.series.id === openId)?.series ?? null;
  }, [section, openId]);

  const onClose = React.useCallback(() => {
    setClicked(null);
    if (!urlInsight) return;
    const next = new URLSearchParams(searchParams?.toString() ?? "");
    next.delete("insight");
    const qs = next.toString();
    router.replace(qs ? `${pathname}?${qs}` : (pathname ?? ""), { scroll: false });
  }, [urlInsight, searchParams, pathname, router]);

  // Fixed overlay — nothing in the page flow depends on it, so no skeleton: while
  // the telemetry read is in flight render nothing (and below xl via CSS).
  if (!hydrated || !data) return null;
  // No stream serves the scope (product/personal/…), or the section is empty ⇒
  // the classic target rows — identical to `dynamic={false}`, its own
  // pulse.aggregate read included.
  if (!section || !hasSeries) {
    return (
      <PulseGutterRailClassic
        scope={scope}
        weight={weight}
        days={days}
        projectSlug={projectSlug}
        className={className}
      />
    );
  }

  const quietCount = section.measured - section.surfaced.length;

  return (
    <aside
      aria-label="Pulse"
      // `data-pulse-rail="peek"` — the resting sliver needs only a NARROW content
      // reservation (globals.css `.page-scroll-row:has([data-pulse-rail="peek"])`);
      // the classic ledger keeps `data-pulse-rail=""` + its wide 208px inset. The
      // popped panel is a fixed overlay, so opening it never reflows the page.
      data-pulse-rail="peek"
      className={cn("pointer-events-none fixed inset-y-0 z-30 hidden items-center xl:flex", className)}
      style={{ right: EDGE_INSET }}
    >
      {/* the morph well — a bare 28px sliver strip at rest, the 208px labeled
          instrument on hover; pointer-events-auto so hovering the strip arms the
          pop (armed only ~1s after load, so page entry never triggers it). */}
      <div
        className={cn("pointer-events-auto relative transition-[width]", MORPH)}
        style={{ width: open ? RAIL_W : PEEK_W }}
        onMouseEnter={enter}
        onMouseMove={enter}
        onMouseLeave={leave}
        onFocusCapture={enter}
        onBlurCapture={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) leave();
        }}
      >
        {/* the popped panel backing — OPAQUE, fades in on open so it reads above
            the page content (the reservation is fixed; the page never reflows). */}
        <div
          aria-hidden
          className={cn(
            "absolute -inset-x-3.5 -bottom-[9px] -top-3 rounded-xl border border-border/60 bg-popover/95 shadow-2xl transition-opacity",
            MORPH,
            open ? "opacity-100" : "opacity-0",
          )}
        />
        <div className="relative">
          {section.anchor && <AnchorPeek anchor={section.anchor} open={open} onOpenInsight={setClicked} />}
          <div className="pt-1">
            {section.surfaced.map((f) => (
              <FindingPeekRow key={f.series.id} f={f} open={open} settled={settled} onOpenInsight={setClicked} />
            ))}
          </div>
          <Collapser open={open}>
            <div className="mt-2 border-t border-border/40 pt-2">
              {/* button-ignore: quiet disclosure toggle — a peek-panel disclosure
                  control (aria-expanded), not a form action; no Button variant
                  applies inside the gutter instrument. */}
              <button
                type="button"
                onClick={() => settled && setQuietOpen((v) => !v)}
                aria-expanded={quietOpen}
                className={cn(
                  "flex w-full items-center justify-between gap-2 text-left type-subtext leading-ui text-muted-foreground/60 outline-none transition-colors focus-visible:ring-1 focus-visible:ring-primary/40",
                  settled ? "cursor-pointer hover:text-muted-foreground" : "cursor-default",
                )}
              >
                <span>{`${quietCount} more measured · in range`}</span>
                <Icon
                  name="chevron-down"
                  size="xs"
                  className={cn("shrink-0 transition-transform", quietOpen && "rotate-180")}
                />
              </button>
              <Collapser open={quietOpen}>
                <div className="pt-1">
                  {section.quiet.map((s) => (
                    <QuietPeekRow key={s.id} s={s} settled={settled} onOpenInsight={setClicked} />
                  ))}
                </div>
              </Collapser>
            </div>
          </Collapser>
        </div>
      </div>
      <InsightDialog
        sec={section}
        days={data.days}
        recentDays={data.recentDays}
        seriesId={openId}
        onClose={onClose}
        actions={
          openSeries ? (
            <InsightActions
              series={openSeries}
              pathname={pathname}
              onWatchChanged={() => mutate()}
            />
          ) : undefined
        }
      />
    </aside>
  );
}

// ── the insight modal footer actions (§4 P4) ──────────────────────────────────
// Watch (pin into `surfaced` regardless of z) · Propose a fix (→ Play). Both are
// secondary/tertiary — `outline`, never a re-painted `default` — per the button
// grammar (no primary on this surface). "Open the filtered view" is no longer a
// footer button: the PeekDialog header's Expand control now owns that route.

function InsightActions({
  series,
  pathname,
  onWatchChanged,
}: {
  series: TelemetrySeriesDTO;
  pathname: string | null;
  onWatchChanged: () => void;
}) {
  const [watchPending, startWatch] = React.useTransition();
  const [proposePending, startPropose] = React.useTransition();
  const [proposed, setProposed] = React.useState(false);

  // Reset the ephemeral "Proposed ✓" state when a different series opens.
  React.useEffect(() => setProposed(false), [series.id]);

  const toggleWatch = () => {
    const next = !series.watched;
    startWatch(async () => {
      try {
        await apiCall(pulseTelemetry.watch, { body: { seriesId: series.id, watched: next } });
        onWatchChanged();
      } catch {
        /* leave the prior state; the toggle is non-destructive and retryable */
      }
    });
  };

  const proposeFix = () => {
    startPropose(async () => {
      try {
        const dir =
          series.recent === null
            ? "unread"
            : series.recent > series.baseline
              ? "up"
              : series.recent < series.baseline
                ? "down"
                : "flat";
        const href = pathname ? `${pathname}?insight=${encodeURIComponent(series.id)}` : undefined;
        await apiCall(play.propose, {
          body: {
            actionKey: `pulse:${series.id}`,
            kind: "fix",
            title: `Investigate: ${series.label} ${dir} (z ${series.z.toFixed(1)})`,
            body: `${fmtTelemetry(series.unit, series.baseline)} normal → ${fmtTelemetry(series.unit, series.recent)} this week — baseline is a 10%-trimmed mean ± robust spread learned from this workspace's own history, no hand-set target.`,
            ctaHref: href,
            ctaLabel: "Open insight",
          },
        });
        setProposed(true);
      } catch {
        /* leave pending false; the button stays actionable for a retry */
      }
    });
  };

  return (
    <>
      <Button variant="outline" size="sm" disabled={watchPending} onClick={toggleWatch}>
        <Icon name="eye" size="xs" />
        {series.watched ? "Watching ✓" : "Watch"}
      </Button>
      <Button variant="outline" size="sm" disabled={proposePending || proposed} onClick={proposeFix}>
        <Icon name="wrench" size="xs" />
        {proposed ? "Proposed ✓" : "Propose a fix"}
      </Button>
    </>
  );
}
